import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import type { TaskFile } from '../../types'
import { getDownloadHref, getDownloadUrl } from '../../api'
import { ImageLightbox } from './ImageLightbox'

interface Props {
  file: TaskFile
  taskId: number
  onDelete: (file: TaskFile) => void
}

/** Pojedynczy plik na liście załączników taska.
 *  - Klik nazwy: pobranie pliku (link href -> 302 -> S3, browser obsługuje natywnie).
 *  - Klik miniatury (image): fetcha pełny presigned URL i otwiera lightbox z dużym obrazkiem. */
export function FileCard({ file, taskId, onDelete }: Props) {
  const downloadHref = getDownloadHref(taskId, file.id)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  const openPreview = async () => {
    setLightboxOpen(true)
    setLightboxUrl(null)
    try {
      const { url } = await getDownloadUrl(taskId, file.id)
      setLightboxUrl(url)
    } catch {
      // fail-safe: zamknij overlay jeśli fetch się wywali
      setLightboxOpen(false)
    }
  }

  const closePreview = () => {
    setLightboxOpen(false)
    setLightboxUrl(null)
  }

  return (
    <div className="flex gap-2 rounded border border-slate-200 p-2 hover:bg-slate-50">
      {file.isImage && file.thumbnailUrl ? (
        <button
          type="button"
          onClick={openPreview}
          className="block h-12 w-12 flex-shrink-0 cursor-zoom-in overflow-hidden rounded"
          title="Powiększ"
        >
          <img
            src={file.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover transition hover:opacity-80"
          />
        </button>
      ) : (
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded bg-slate-100 text-xs text-slate-500">
          {fileIconLabel(file.contentType)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <a
          href={downloadHref}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-sm font-medium text-sky-700 hover:underline"
        >
          {file.filename}
        </a>
        <div className="text-xs text-slate-500">
          {formatSize(file.sizeBytes)}
          {file.documentTypeLabel && (
            <span className="ml-2">· {file.documentTypeLabel}</span>
          )}
        </div>
        <div className="truncate text-xs text-slate-400">
          {file.uploadedByUserName} · {new Date(file.uploadedAt).toLocaleString('pl')}
        </div>
        {file.description && (
          <div className="mt-1 text-xs italic text-slate-600">{file.description}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDelete(file)}
        className="self-start p-1 text-slate-400 hover:text-red-600"
        title="Usuń"
      >
        <Trash2 className="h-4 w-4" />
      </button>
      {lightboxOpen && (
        <ImageLightbox
          src={lightboxUrl}
          filename={file.filename}
          onClose={closePreview}
        />
      )}
    </div>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function fileIconLabel(contentType: string): string {
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType.includes('word')) return 'DOC'
  if (contentType.includes('sheet') || contentType.includes('excel')) return 'XLS'
  return 'FILE'
}
